<template>
    <div class="asset-stats">
        <v-row class="asset-stats-chart">
        <v-col cols="12">
            <h3 class="mb-3">{{ $t('assetStats.title') }}</h3>
        </v-col>
            <v-col cols="12" md="3" class="asset-chart-col">
                <div class="asset-chart-header">
                    <h3>{{ $t('market.title') }}</h3>
                    <p>{{ $t('market.subtitle') }}</p>
                </div>
                <apexchart class="asset-donut-chart d-flex justify-center" type="donut" width="100%" height="280"
                    :options="optionsMarketDistribution" :series="seriesMarkets" />
                <ul class="chart-legend">
                    <li v-for="(item, index) in marketLegendItems" :key="'market-' + index" class="legend-row">
                        <span class="legend-label">
                            <span class="legend-dot" :style="{ backgroundColor: item.color }"></span>
                            {{ item.label }}
                        </span>
                        <span class="legend-value legend-value--neutral">{{ item.value }}</span>
                    </li>
                </ul>
            </v-col>
            <v-col cols="12" md="3" class="asset-chart-col">
                <div class="asset-chart-header">
                    <h3>{{ $t('allocation.title') }}</h3>
                    <p>{{ $t('allocation.subtitle') }}</p>
                </div>
                <apexchart class="asset-donut-chart d-flex justify-center" width="100%" height="280" type="donut"
                    :options="optionsAssetDistribution" :series="seriesAssetDistribution" />
                <ul class="chart-legend">
                    <li v-for="(item, index) in allocationLegendItems" :key="'allocation-' + index" class="legend-row">
                        <span class="legend-label">
                            <span class="legend-dot" :style="{ backgroundColor: item.color }"></span>
                            {{ item.label }}
                        </span>
                        <span class="legend-value legend-value--neutral">{{ item.value }}</span>
                    </li>
                </ul>
            </v-col>
            <v-col cols="12" md="3" class="asset-chart-col">
                <div class="asset-chart-header">
                    <h3>{{ $t('profits.title') }}</h3>
                    <p>{{ $t('profits.subtitle') }}</p>
                </div>
                <apexchart class="asset-donut-chart d-flex justify-center" type="donut" width="100%" height="280"
                    :options="optionsProfitContribution" :series="seriesProfitContribution" />
                <ul class="chart-legend">
                    <li v-for="(item, index) in profitLegendItems" :key="'profit-' + index" class="legend-row">
                        <span class="legend-label">
                            <span class="legend-dot" :style="{ backgroundColor: item.color }"></span>
                            {{ item.label }}
                        </span>
                        <span class="legend-value legend-value--profit">{{ item.value }}</span>
                    </li>
                </ul>
            </v-col>
            <v-col cols="12" md="3" class="asset-chart-col">
                <div class="asset-chart-header">
                    <h3>{{ $t('losses.title') }}</h3>
                    <p>{{ $t('losses.subtitle') }}</p>
                </div>
                <apexchart class="asset-donut-chart d-flex justify-center" type="donut" width="100%" height="280"
                    :options="optionsLossesContribution" :series="seriesLossesContribution" />
                <ul class="chart-legend">
                    <li v-for="(item, index) in lossLegendItems" :key="'loss-' + index" class="legend-row">
                        <span class="legend-label">
                            <span class="legend-dot" :style="{ backgroundColor: item.color }"></span>
                            {{ item.label }}
                        </span>
                        <span class="legend-value legend-value--loss">{{ item.value }}</span>
                    </li>
                </ul>
            </v-col>
        </v-row>

        <JournalInsightBar :insight="insight" />
    </div>
</template>

<script>
import JournalInsightBar from '@/views/Journal/components/JournalInsightBar';

const BLUE_COLORS = ['#AFCBFA', '#4F9CF9', '#1A56DB', '#0F3D99', '#082F6B'];
const GREEN_COLORS = ['rgb(0,85,50)', 'rgb(36,120,81)', 'rgb(0,196,105)', 'rgba(0, 247, 132, 1)', '#95f9ca'];
const LOSS_COLORS = ['rgb(67,11,0)', '#b22400', '#d76e00', '#b28000'];

const HIDDEN_LEGEND = { show: false };

export default {
    name: "AssetStats",
    components: { JournalInsightBar },
    props: {
        // AI insight for this section, already resolved by JournalStats (null = nothing to show).
        insight: { type: Object, default: null },
    },
    data() {
        return {
            // Options
            optionsProfitContribution: {
                colors: ['rgba(0, 247, 132, 1)', 'rgb(0,179,247)', 'rgb(136,0,247)', 'rgb(208,0,247)'],
                labels: [],
            },
            optionsLossesContribution: {
                colors: ['rgba(0, 247, 132, 1)', 'rgb(0,179,247)', 'rgb(136,0,247)', 'rgb(208,0,247)'],
                labels: [],
            },
            optionsMarketDistribution: {
                colors: BLUE_COLORS,
                labels: [],
            },
            optionsAssetDistribution: {
                theme: {
                    mode: this.$store.state.currentTheme == "dark" ? "dark" : "light"
                },
                noData: {
                    text: 'No data for the current filter',
                    align: 'center',
                    verticalAlign: 'middle',
                    style: {
                        color: '#111111',
                        fontSize: '21px',
                        fontFamily: 'Ubuntu, sans-serif',
                    }
                },
                legend: HIDDEN_LEGEND,
                colors: BLUE_COLORS,
                labels: [],
            },

            //Series
            seriesProfitContribution: [],
            seriesLossesContribution: [],
            seriesMarkets: [],
            seriesAssetDistribution: [],
        };
    },
    computed: {
        marketLegendItems() {
            return this.buildLegendItems(
                this.optionsMarketDistribution.labels,
                this.seriesMarkets,
                this.optionsMarketDistribution.colors || BLUE_COLORS,
                'count'
            );
        },
        allocationLegendItems() {
            return this.buildLegendItems(
                this.optionsAssetDistribution.labels,
                this.seriesAssetDistribution,
                this.optionsAssetDistribution.colors || BLUE_COLORS,
                'count'
            );
        },
        profitLegendItems() {
            return this.buildLegendItems(
                this.optionsProfitContribution.labels,
                this.seriesProfitContribution,
                this.optionsProfitContribution.colors || GREEN_COLORS,
                'profit'
            );
        },
        lossLegendItems() {
            return this.buildLegendItems(
                this.optionsLossesContribution.labels,
                this.seriesLossesContribution,
                this.optionsLossesContribution.colors || LOSS_COLORS,
                'loss'
            );
        },
    },
    mounted() {
        this.setData();
    },
    watch: {
        '$store.state.book.trades': function () {
            this.setData();
        }
    },
    methods: {
        buildLegendItems(labels, series, colors, format) {
            if (!labels || !labels.length) {
                return [];
            }
            return labels.map((label, index) => {
                const rawValue = series[index];
                let value = rawValue;
                if (format === 'count') {
                    value = String(rawValue);
                } else if (format === 'profit') {
                    value = `+$${Number(rawValue).toFixed(2)}`;
                } else if (format === 'loss') {
                    value = `-$${Number(rawValue).toFixed(2)}`;
                }
                return {
                    label,
                    value,
                    color: colors[index % colors.length],
                };
            });
        },
        setData() {
            this.setContribution();
            this.setAssetDistributionChart();
        },
        setContribution() {
            let mapProfit = {};
            let mapLosses = {};
            let mapMarkets = {};

            let labelsProfit = [];
            let labelsLosses = [];
            let labelsMarkets = [];
            this.seriesProfitContribution = [];
            this.seriesLossesContribution = [];
            this.seriesMarkets = [];

            let copyOfTrades = this.$store.state.book.trades.slice();

            copyOfTrades.forEach((trade) => {
                // Monetary Contribution
                if (!(trade.asset.ticker in mapProfit) && trade.netProfit > 0)
                    mapProfit[trade.asset.ticker] = trade.netProfit;
                else if (!(trade.asset.ticker in mapLosses) && trade.netProfit <= 0)
                    mapLosses[trade.asset.ticker] = trade.netProfit;
                else if (trade.netProfit > 0)
                    mapProfit[trade.asset.ticker] = ((mapProfit[trade.asset.ticker] * 1) + trade.netProfit);
                else
                    mapLosses[trade.asset.ticker] = ((mapLosses[trade.asset.ticker] * 1) + trade.netProfit);

                // Market Distribution
                if (trade.asset.market != null) {
                    if (!(trade.asset.market.name in mapMarkets))
                        mapMarkets[trade.asset.market.name] = 1;
                    else
                        mapMarkets[trade.asset.market.name] = ((mapMarkets[trade.asset.market.name] * 1) + 1);
                }
            });

            for (let asset in mapProfit) {
                labelsProfit.push(asset);
                this.seriesProfitContribution.push(mapProfit[asset]);
            }
            for (let asset in mapLosses) {
                labelsLosses.push(asset);
                this.seriesLossesContribution.push(Math.abs(mapLosses[asset]));
            }
            for (let market in mapMarkets) {
                labelsMarkets.push(market);
                this.seriesMarkets.push(mapMarkets[market]);
            }

            this.optionsProfitContribution = {
                theme: {
                    mode: this.$store.state.currentTheme == "dark" ? "dark" : "light"
                },
                noData: {
                    text: 'No data for the current filter',
                    align: 'center',
                    verticalAlign: 'middle',
                    style: {
                        color: '#111111',
                        fontSize: '21px',
                        fontFamily: 'Ubuntu, sans-serif',
                    }
                },
                labels: labelsProfit,
                legend: HIDDEN_LEGEND,
                colors: GREEN_COLORS,
                tooltip: {
                    y: {
                        formatter: function (val) {
                            return `$ ${(val === undefined) ? val : val.toFixed(2)}`;
                        },
                        title: {
                            formatter: function (asset) {
                                return asset;
                            }
                        }
                    }
                },
            };
            this.optionsLossesContribution = {
                theme: {
                    mode: this.$store.state.currentTheme == "dark" ? "dark" : "light"
                },
                noData: {
                    text: 'No data for the current filter',
                    align: 'center',
                    verticalAlign: 'middle',
                    style: {
                        color: '#111111',
                        fontSize: '21px',
                        fontFamily: 'Ubuntu, sans-serif',
                    }
                },
                labels: labelsLosses,
                legend: HIDDEN_LEGEND,
                colors: LOSS_COLORS,
                tooltip: {
                    y: {
                        formatter: function (val) {
                            return `-$ ${(val === undefined) ? val : val.toFixed(2)}`;
                        },
                        title: {
                            formatter: function (asset) {
                                return asset;
                            }
                        }
                    }
                },
            };
            this.optionsMarketDistribution = {
                theme: {
                    mode: this.$store.state.currentTheme == "dark" ? "dark" : "light"
                },
                noData: {
                    text: 'No data for the current filter',
                    align: 'center',
                    verticalAlign: 'middle',
                    style: {
                        color: '#111111',
                        fontSize: '21px',
                        fontFamily: 'Ubuntu, sans-serif',
                    }
                },
                labels: labelsMarkets,
                legend: HIDDEN_LEGEND,
                colors: BLUE_COLORS,
                tooltip: {
                    y: {
                        formatter: function (val) {
                            return `${val} trade(s)`;
                        },
                        title: {
                            formatter: function (market) {
                                return market;
                            }
                        }
                    }
                },
            };
        },
        setAssetDistributionChart() {
            let map = {};
            this.seriesAssetDistribution = [];
            let newLabels = [];

            let copyOfTrades = this.$store.state.book.trades.slice();
            copyOfTrades.forEach((trade) => {
                if (!(trade.asset.ticker in map))
                    map[trade.asset.ticker] = 1;
                else
                    map[trade.asset.ticker] = ((map[trade.asset.ticker] * 1) + 1);
            });

            for (let asset in map) {
                newLabels.push(asset);
                this.seriesAssetDistribution.push(map[asset]);
            }

            this.optionsAssetDistribution = {
                theme: {
                    mode: this.$store.state.currentTheme == "dark" ? "dark" : "light"
                },
                noData: {
                    text: 'No data for the current filter',
                    align: 'center',
                    verticalAlign: 'middle',
                    style: {
                        color: '#111111',
                        fontSize: '21px',
                        fontFamily: 'Ubuntu, sans-serif',
                    }
                },
                labels: newLabels,
                legend: HIDDEN_LEGEND,
                colors: BLUE_COLORS,
                tooltip: {
                    y: {
                        formatter: function (val) {
                            return `${val} trades`;
                        },
                        title: {
                            formatter: function (asset) {
                                return asset;
                            }
                        }
                    }
                },
            };
        },
    },
}
</script>



<style lang="scss">
.app {
    .asset-stats-chart {
        .asset-chart-col {
            display: flex;
            flex-direction: column;
        }

        .asset-chart-header {
            min-height: 60px;
            margin-bottom: 12px;
            text-align: center;

            h3 {
                margin: 0 0 8px;
                font-family: 'Avenir', sans-serif;
                font-weight: 700;
                font-size: 16px;
                line-height: 1.3;
                color: var(--v-mainBlack-base);
            }

            p {
                margin: 0;
                font-family: 'Avenir', sans-serif;
                font-size: 14px;
                line-height: 1.4;
                color: var(--v-mainBlack-base);
            }
        }

        .asset-donut-chart {
            width: 100%;
            max-width: 280px;
            margin: 0 auto;
        }

        .chart-legend {
            list-style: none;
            padding: 12px 8px 0;
            margin: 0 auto;
            width: 100%;
            max-width: 280px;
        }

        .legend-row {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 12px;
            padding: 6px 0;
        }

        .legend-label {
            display: flex;
            align-items: center;
            gap: 8px;
            font-family: 'Avenir', sans-serif;
            font-size: 14px;
            color: var(--v-mainBlack-base);
            min-width: 0;
        }

        .legend-dot {
            flex-shrink: 0;
            width: 15px;
            height: 15px;
            border-radius: 50%;
        }

        .legend-value {
            flex-shrink: 0;
            font-family: 'Avenir', sans-serif;
            font-size: 14px;
            font-weight: 600;

            &--neutral {
                color: #ABABAB !important;
            }

            &--profit {
                color: #005931 !important;
            }

            &--loss {
                color: #D00808 !important;
            }
        }

        .apexcharts-text {
            fill: var(--v-white-base);
        }
    }
}
</style>

